/**
 * Filesystem utilities for Emscripten virtual filesystem operations.
 * Centralizes all filesystem management logic for MuJoCo.
 */

import type { MainModule, EmscriptenFS } from './mujocoTypes';

/**
 * Generates a unique directory path for a TAR file to prevent collisions.
 * Uses base64 encoding of the tar path as a hash.
 *
 * @param tarPath - Path to the TAR file
 * @returns Unique working directory path
 */
export function getWorkingDirForTar(tarPath: string): string {
  // Create a simple hash from the tar path
  const hash = btoa(tarPath).replace(/[=+/]/g, '').substring(0, 16);
  return `/working/${hash}`;
}

/**
 * Ensures a directory exists in the virtual filesystem, creating it if necessary.
 * Creates parent directories as needed.
 *
 * @param fs - The Emscripten filesystem module
 * @param dirPath - Path to the directory to ensure exists
 */
export function ensureDirectory(fs: MainModule['FS'], dirPath: string): void {
  const typedFS = fs as unknown as EmscriptenFS;

  // Ensure /working exists first (common base directory)
  if (dirPath.startsWith('/working')) {
    try {
      typedFS.stat('/working');
    } catch {
      typedFS.mkdir('/working');
    }
  }

  // Then ensure the specific directory
  if (dirPath !== '/working') {
    try {
      typedFS.stat(dirPath);
    } catch {
      typedFS.mkdir(dirPath);
    }
  }
}

/**
 * Creates a directory and all parent directories as needed.
 * Similar to `mkdir -p` in Unix.
 *
 * @param fs - The Emscripten filesystem module
 * @param dirPath - Path to create (can be nested)
 * @param basePath - Base path to start from (default: '')
 */
export function mkdirRecursive(fs: MainModule['FS'], dirPath: string, basePath: string = ''): void {
  const typedFS = fs as unknown as EmscriptenFS;

  // Split path and filter out empty parts
  const parts = dirPath.split('/').filter((d) => d);
  let currentPath = basePath;

  for (const part of parts) {
    currentPath += `/${part}`;
    try {
      typedFS.mkdir(currentPath);
    } catch {
      // Directory might already exist, ignore error
    }
  }
}
